import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PERMISSIONS } from '@wms/shared';
import { ApiError } from '../../lib/api';
import { useElevatedFetch } from '../../lib/elevation';
import { useInvalidateSession, useSession } from '../../lib/session';

/**
 * Override PIN 自助管理（D-02）。UI 入口本身就是第一道防线——只有持有
 * `inventory.override.negative` 的用户会在 `/session/me` 里看到这项权限，
 * 后端在 `UsersService.setOverridePin` 里独立再校验一次（belt and braces，
 * 不信任前端）。
 */
export function OverridePinPanel(): React.JSX.Element | null {
  const { t } = useTranslation();
  const { data: session } = useSession();
  const elevatedFetch = useElevatedFetch();
  const invalidateSession = useInvalidateSession();

  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (!session?.permissions.includes(PERMISSIONS.INVENTORY_OVERRIDE_NEGATIVE)) return null;

  const submitSet = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    if (pin !== confirmPin) {
      setError(t('settings.pinMismatch'));
      return;
    }
    setSubmitting(true);
    try {
      await elevatedFetch(PERMISSIONS.OVERRIDE_PIN_MANAGE, '/users/me/override-pin', { pin });
      setPin('');
      setConfirmPin('');
      setSuccess(t('settings.pinSetSuccess'));
      await invalidateSession();
    } catch (err) {
      if (!(err instanceof Error && err.message === 'elevation_cancelled')) {
        setError(err instanceof ApiError ? t(`errors:${err.code}`, err.params) : String(err));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const clear = async (): Promise<void> => {
    if (!confirm(t('settings.pinClearConfirm'))) return;
    try {
      await elevatedFetch(PERMISSIONS.OVERRIDE_PIN_MANAGE, '/users/me/override-pin', {}, 'DELETE');
      setSuccess(t('settings.pinClearSuccess'));
      await invalidateSession();
    } catch (err) {
      if (!(err instanceof Error && err.message === 'elevation_cancelled')) {
        setError(err instanceof ApiError ? t(`errors:${err.code}`, err.params) : String(err));
      }
    }
  };

  return (
    <section className="section">
      <h2>{t('settings.pinTitle')}</h2>
      <p className="field-hint">{t('settings.pinDescription')}</p>
      <form onSubmit={(e) => void submitSet(e)}>
        <label className="field">
          <span>{t('settings.pinNewLabel')}</span>
          <input
            type="password"
            inputMode="numeric"
            pattern="\d{4,8}"
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            required
          />
        </label>
        <label className="field">
          <span>{t('settings.pinConfirmLabel')}</span>
          <input
            type="password"
            inputMode="numeric"
            pattern="\d{4,8}"
            value={confirmPin}
            onChange={(e) => setConfirmPin(e.target.value)}
            required
          />
        </label>
        {error && <p className="field-error">{error}</p>}
        {success && <p className="badge badge--success">{success}</p>}
        <div className="row">
          <button type="submit" className="btn btn--primary" disabled={submitting}>
            {session.hasOverridePin ? t('settings.pinChange') : t('settings.pinSet')}
          </button>
          {session.hasOverridePin && (
            <button type="button" className="btn btn--danger" onClick={() => void clear()}>
              {t('settings.pinClear')}
            </button>
          )}
        </div>
      </form>
    </section>
  );
}
